/**
 * EffectsEditor — button effects: opacity + v2.17 Enter/Tap/Idle animation presets
 *
 * Reworked (animation-section UX cleanup, approved mock
 * .design/screen-edit-animation-section/):
 * - NO master "Enable effects" checkbox — the old gate existed only to write
 *   `opacity = 1` (+ a legacy shimmer default). Fields are individually nullable:
 *   - Opacity: its own checkbox-gated row (on → 1, off → null)
 *   - Enter / Tap / Idle: always-visible selects defaulting to "None" (null)
 * - The legacy `animation.kind` select renders ONLY when a legacy value is
 *   already set AND none of the new enter/tap/idle presets are — legacy data
 *   stays editable, clean buttons never see the control, and no data is
 *   migrated. Runtime already prioritizes the new fields over legacy.
 *
 * Paths (written by the wrapping factory):
 * - appearance.opacity (number | null)
 * - appearance.animation.kind (string | null) — legacy
 * - appearance.{enter|tap|idle}Animation ({ preset } | null) — v2.17
 *
 * TestId convention: `style-group-effects-{control}`
 */

import React, { useCallback } from 'react';
import {
  Box,
  Checkbox,
  Typography,
  Slider,
  Select,
  MenuItem,
} from '@mui/material';
import {
  checkboxIconSx,
  checkboxCheckedIconSx,
  checkboxSx,
  fieldLabelSx,
  STYLE_FIELD_LABEL_HEIGHT,
} from '../../../config/editorFieldStyles';
import { FIELD_GAP_SMALL } from '../../../config/ui';
import {
  V2_ENTRANCE_PRESET_OPTIONS,
  V2_EMPHASIS_PRESET_OPTIONS,
  V2_IDLE_PRESET_OPTIONS,
} from '../../../config/v2AnimationPresetOptions';

const ANIMATION_OPTIONS = [
  { value: '', label: 'None' },
  { value: 'pulse', label: 'Pulse' },
  { value: 'shimmer', label: 'Shimmer' },
  { value: 'bounce', label: 'Bounce' },
  { value: 'shake', label: 'Shake' },
  { value: 'glow', label: 'Glow' },
  { value: 'ripple', label: 'Ripple' },
  { value: 'scale', label: 'Scale' },
  { value: 'fadeIn', label: 'Fade In' },
  { value: 'slideUp', label: 'Slide Up' },
  { value: 'wiggle', label: 'Wiggle' },
];

export interface EffectsEditorProps {
  /** Current opacity value (null = not set) */
  opacity: number | null | undefined;
  /** Current animation kind (null/empty = no animation) */
  animationKind: string | null | undefined;
  /** Callback for opacity change */
  onOpacityChange: (value: number | null) => void;
  /** Callback for animation kind change */
  onAnimationChange: (value: string | null) => void;
  /** v2.17 enter animation preset (null/empty = none). Priority over legacy animation. */
  enterPreset?: string | null;
  /** v2.17 tap animation preset (plays on click) */
  tapPreset?: string | null;
  /** v2.17 idle animation preset (infinite loop) */
  idlePreset?: string | null;
  /** Callbacks for the v2.17 preset selects — omit to hide the new rows */
  onEnterPresetChange?: (value: string | null) => void;
  onTapPresetChange?: (value: string | null) => void;
  onIdlePresetChange?: (value: string | null) => void;
  disabled?: boolean;
}

export const EffectsEditor: React.FC<EffectsEditorProps> = ({
  opacity,
  animationKind,
  onOpacityChange,
  onAnimationChange,
  enterPreset,
  tapPreset,
  idlePreset,
  onEnterPresetChange,
  onTapPresetChange,
  onIdlePresetChange,
  disabled = false,
}) => {
  const isOpacitySet = opacity != null;

  // Legacy select: only for buttons that already carry a legacy kind and no
  // new-style preset. Consumers without the new callbacks (legacy-only
  // surfaces) always keep the select when a kind is set.
  const hasNewPreset = Boolean(enterPreset || tapPreset || idlePreset);
  const hasNewControls = Boolean(onEnterPresetChange || onTapPresetChange || onIdlePresetChange);
  const showLegacySelect = Boolean(animationKind) && (!hasNewControls || !hasNewPreset);

  const handleOpacityToggle = useCallback(
    (_: unknown, checked: boolean) => {
      // Pure nullable field: on → full opacity, off → null. Nothing else is
      // written — enabling effects must not plant a legacy shimmer default.
      onOpacityChange(checked ? 1 : null);
    },
    [onOpacityChange],
  );

  const handleOpacitySlider = useCallback(
    (_: unknown, val: number | number[]) => {
      onOpacityChange((val as number) / 100);
    },
    [onOpacityChange],
  );

  const handleAnimationSelect = useCallback(
    (e: { target: { value: unknown } }) => {
      const kind = e.target.value as string;
      onAnimationChange(kind || null);
    },
    [onAnimationChange],
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }} data-testid="style-group-effects">
      {/* Opacity — nullable checkbox row */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: FIELD_GAP_SMALL, height: STYLE_FIELD_LABEL_HEIGHT, minHeight: STYLE_FIELD_LABEL_HEIGHT }}>
          <Checkbox
            size="small"
            checked={isOpacitySet}
            onChange={handleOpacityToggle}
            disabled={disabled}
            icon={<Box sx={checkboxIconSx} />}
            checkedIcon={<Box sx={checkboxCheckedIconSx}>✓</Box>}
            sx={checkboxSx}
            data-testid="style-group-effects-opacity-enable"
          />
          <Typography variant="caption" color="text.secondary" sx={fieldLabelSx}>
            Opacity
          </Typography>
        </Box>
        {isOpacitySet && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Slider
              size="small"
              value={Math.round((opacity ?? 1) * 100)}
              onChange={handleOpacitySlider}
              min={0}
              max={100}
              disabled={disabled}
              sx={{ flex: 1 }}
              data-testid="style-group-effects-opacity"
            />
            <Typography variant="caption" sx={{ minWidth: 28, textAlign: 'right' }}>
              {Math.round((opacity ?? 1) * 100)}%
            </Typography>
          </Box>
        )}
      </Box>

      {/* v2.17 enter/tap/idle preset animations — always visible */}
      {onEnterPresetChange && (
        <PresetSelectRow
          label="Enter"
          value={enterPreset}
          options={V2_ENTRANCE_PRESET_OPTIONS}
          onChange={onEnterPresetChange}
          disabled={disabled}
          testId="style-group-effects-enter-animation"
        />
      )}
      {onTapPresetChange && (
        <PresetSelectRow
          label="Tap"
          value={tapPreset}
          options={V2_EMPHASIS_PRESET_OPTIONS}
          onChange={onTapPresetChange}
          disabled={disabled}
          testId="style-group-effects-tap-animation"
        />
      )}
      {onIdlePresetChange && (
        <PresetSelectRow
          label="Idle"
          value={idlePreset}
          options={V2_IDLE_PRESET_OPTIONS}
          onChange={onIdlePresetChange}
          disabled={disabled}
          testId="style-group-effects-idle-animation"
        />
      )}

      {/* Legacy animation kind — legacy-data buttons only */}
      {showLegacySelect && (
        <Box sx={{ borderTop: '1px dashed', borderColor: 'divider', pt: 1 }}>
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              fontSize: '10px',
              display: 'block',
              mb: 0.5,
            }}
          >
            Legacy
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" sx={{ minWidth: 52 }}>
              Animation
            </Typography>
            <Select
              size="small"
              value={animationKind ?? ''}
              onChange={handleAnimationSelect}
              disabled={disabled}
              data-testid="style-group-effects-animation"
              sx={{ flex: 1, fontSize: 12, '& .MuiSelect-select': { py: 0.5 } }}
            >
              {ANIMATION_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: 12 }}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </Box>
      )}
    </Box>
  );
};

/** Shared select row for the v2.17 preset pickers ("" option = None → null) */
const PresetSelectRow: React.FC<{
  label: string;
  value: string | null | undefined;
  options: ReadonlyArray<{ value: string; label: string }>;
  onChange: (value: string | null) => void;
  disabled: boolean;
  testId: string;
}> = ({ label, value, options, onChange, disabled, testId }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
    <Typography variant="caption" sx={{ minWidth: 52 }}>
      {label}
    </Typography>
    <Select
      size="small"
      value={value ?? ''}
      onChange={(e) => onChange((e.target.value as string) || null)}
      disabled={disabled}
      data-testid={testId}
      sx={{ flex: 1, fontSize: 12, '& .MuiSelect-select': { py: 0.5 } }}
    >
      <MenuItem value="" sx={{ fontSize: 12 }}>None</MenuItem>
      {options.map((opt) => (
        <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: 12 }}>
          {opt.label}
        </MenuItem>
      ))}
    </Select>
  </Box>
);
